/**
 * Single source of truth for a tool call's *structural identity* and the
 * repeated-failure guard built on it.
 *
 * Why this exists: an agent that keeps re-issuing the same failing tool
 * call with cosmetically-different formatting (whitespace, key order)
 * never escapes the loop, because each attempt looks "new". Identity is
 * computed with TanStack Query's deterministic `hashKey` (object-key-order
 * insensitive), and tools that take an s-expression normalise it to its
 * parsed AST so two semantically-identical expressions that differ only
 * in formatting hash identically.
 *
 * The expression-shape knowledge lives in `expressionArgsIdentity`, which
 * a tool *opts into* declaratively via the `<Tool identityKey=…>` prop —
 * generic consumers never hard-code "does this tool have an `expression`
 * field".
 */

import { hashKey } from '@tanstack/react-query';

import { type AstInput, normalize } from '#core/lib/expr/normalize.ts';
import { parse } from '#core/lib/expr/parser.ts';

import { isToolCallPart, type ToolOutputError } from '#plugin-chat/lib/tool/output-types.ts';
import { isFailurePart, parseResultEnvelope } from '#plugin-chat/lib/tool/result-envelope.ts';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * Turn a producer's `expression` string into its parsed, normalized
 * `AstInput` — the SSOT for "s-expr string → identity AST". A tool call
 * is a single top-level form; the parser emits no Date/Dataset literals,
 * so the result is JSON-safe and stable under TanStack's `hashKey`, so
 * formatting-only differences hash identically.
 */
export const expressionToAst = (expression: string): AstInput => normalize(parse(expression)).ast[0] ?? null;

/**
 * Normalises a tool call's args into a stable, formatting-insensitive
 * shape before hashing. Default identity hashes args as-is; tools with
 * special-cased fields supply their own.
 */
export type ToolArgsIdentity = (args: unknown) => unknown;

/**
 * Args normaliser for s-expression tools: replace the `expression`
 * string with its parsed AST so whitespace / formatting differences do
 * not change identity. A genuinely unparseable expression (syntax
 * error) falls back to the raw string, so two byte-identical bad calls
 * still match. This is the shared helper s-expr tools pass as their
 * `<Tool identityKey=…>` — the only place that knows about `expression`.
 */
export const expressionArgsIdentity: ToolArgsIdentity = (args) => {
  if (!isObject(args) || typeof args.expression !== 'string') {
    return args;
  }
  try {
    return { ...args, expression: expressionToAst(args.expression) };
  } catch {
    return args;
  }
};

/**
 * Tools whose call is "evaluate this s-expression": they share the
 * `expressionArgsIdentity` normaliser and run the identical expression through
 * the same engine, so the repeated-failure guard must treat them as ONE
 * logical operation. Otherwise the model dodges the guard by re-issuing the
 * same failing expression under a sibling tool (transform → query → transform).
 * Mirrors the JSX `name` each tool registers under — same hardcoded-name
 * pattern as `PRODUCER_TOOL_NAMES` in the transport layer.
 */
const EXPRESSION_TOOL_NAMES: ReadonlySet<string> = new Set([
  'transformDataset',
  'queryKnowledgeGraph',
  'showDashboard',
]);

/** The dedup group every expression tool collapses to. */
const EXPRESSION_TOOL_GROUP = 'expression-eval';

/**
 * The identity GROUP a tool's repeated-failure key is bucketed under: every
 * expression tool maps to one shared group; any other tool keys on its own
 * name (so the grouping is a no-op for non-expression tools).
 */
export const identityGroupFor = (toolName: string): string =>
  EXPRESSION_TOOL_NAMES.has(toolName) ? EXPRESSION_TOOL_GROUP : toolName;
/**
 * Canonical structural identity for a tool call: identity GROUP + normalised
 * args. SSOT for both the repeated-failure guard (`runToolCall`) and the
 * transcript log collapse (`Transcript`). When an `argsIdentity`
 * normaliser is supplied (the expression tools), the key is the tool's
 * identity GROUP rather than its name — so the SAME expression run under any
 * expression tool hashes identically and the failure guard cannot be dodged by
 * switching tools. Without a normaliser (the transcript path) the key
 * stays the literal tool name, so unrelated tools never collapse.
 */
export const toolCallIdentity = (toolName: string, args: unknown, argsIdentity?: ToolArgsIdentity): string =>
  argsIdentity ? hashKey([identityGroupFor(toolName), argsIdentity(args)]) : hashKey([toolName, args]);

export type ThreadMessageLike = { readonly content?: readonly unknown[] };

/**
 * Has the same tool already been called on this thread with an identical
 * identity and *failed*? The in-flight call's own part is not yet on the
 * thread when `runToolCall` runs, so a match here means the just-failed
 * call is at least the 2nd identical failing attempt. Candidates are matched
 * by identity GROUP, not exact tool name, so the same failing expression is
 * caught across the expression tools even when the model switches between them
 * (see `identityGroupFor`).
 */
export const hasPriorIdenticalFailure = (
  messages: readonly ThreadMessageLike[],
  toolName: string,
  identity: string,
  argsIdentity?: ToolArgsIdentity,
): boolean =>
  messages.some((message) =>
    (message.content ?? []).some(
      (candidate) =>
        isToolCallPart(candidate) &&
        identityGroupFor(candidate.toolName) === identityGroupFor(toolName) &&
        isFailurePart(candidate) &&
        toolCallIdentity(candidate.toolName, candidate.args, argsIdentity) === identity,
    ),
  );

/** SSOT marker separating the step-back directive from the underlying error. */
const ORIGINAL_ERROR_MARKER = '\n\nOriginal error: ';

/** Default count of prior same-signature failures that trips the step-back directive. */
export const DEFAULT_REPEAT_SIGNATURE_THRESHOLD = 2;

/**
 * Reduce a failure message to a stable locus for signature bucketing: drop any
 * prepended step-back directive (keep only the underlying error)
 * and strip any leading `line:col:` source position. So the same mistake —
 * re-flagged on a later attempt, or reported at a shifted position — hashes
 * identically.
 */
const failureLocus = (message: string): string => {
  const markerAt = message.indexOf(ORIGINAL_ERROR_MARKER);
  const underlying = markerAt === -1 ? message : message.slice(markerAt + ORIGINAL_ERROR_MARKER.length);
  return underlying.replace(/^\d+:\d+:\s*/, '');
};

/**
 * A coarse, formatting-insensitive failure signature: identity GROUP +
 * errorType + the error's {@link failureLocus}. Unlike {@link toolCallIdentity}
 * (which keys on the exact normalised expression AST), this collapses "the
 * same error, lightly varied" — e.g. an `unknown column` re-raised against a
 * different upstream binding — so a retry loop that mutates the call without
 * addressing the cause is still caught.
 */
export const failureSignature = (toolName: string, envelope: ToolOutputError): string =>
  hashKey([identityGroupFor(toolName), envelope.errorType, failureLocus(envelope.error)]);

/**
 * How many settled failures already on the thread share `signature`. The
 * in-flight call's own part is not yet on the thread, so a return of N means
 * the just-failed call is the (N+1)th with this signature. Thread-wide (not
 * consecutive) — interleaved successes (e.g. noop reshapes between retries)
 * never reset it, mirroring {@link hasPriorIdenticalFailure}.
 */
export const priorFailureSignatureCount = (messages: readonly ThreadMessageLike[], signature: string): number =>
  messages.reduce(
    (acc, message) =>
      acc +
      (message.content ?? []).filter((candidate): boolean => {
        if (!isToolCallPart(candidate) || candidate.result === undefined) {
          return false;
        }
        const parsed = parseResultEnvelope(candidate.result);
        return parsed.kind === 'failure' && failureSignature(candidate.toolName, parsed.error) === signature;
      }).length,
    0,
  );
const REPEATED_FAILURE_DIRECTIVE =
  'You have already hit this same failure on this thread, and retrying with the same approach — even lightly varied — is not working. ' +
  'Step back and address the underlying cause before calling again: rethink the expression, inspect the actual schema and values, or change strategy. ' +
  'Note that renaming a column never changes its type — if an op rejects a column (e.g. a struct/relation with "no natural order"), reference a scalar subfield or derive one; do not rename and retry. ' +
  'If you cannot find a working call, report back to the user instead of retrying again.';

/**
 * Error-specific, actionable hints appended to the generic directive when a
 * repeated failure is a recognizable DSL error. A lookup table (not an
 * if/else chain) keyed by a substring match — first match wins.
 */
const REPEATED_FAILURE_HINTS: readonly { readonly match: string; readonly hint: string }[] = [
  {
    match: 'column/path labels must be keywords, symbols, or string literals',
    hint:
      'To read a field from a relation-list / array<struct> column you NAME the field — `(col % assets name)` maps it across every element (→ array) — never use an integer index. ' +
      'Use `unroll` to fan out the array into rows, or `str-join`/`first`/`last`/`nth` to collapse it.',
  },
  {
    match: 'unknown op:',
    hint: 'Check the op name against the reference. There is no positional array indexing; `first`/`last`/`nth`/`str-join` exist for arrays, and `(col % <rel> <field>)` projects a field across a relation list.',
  },
];

/**
 * Maps a repeated-failure error message to an optional actionable hint. Pure
 * lookup over {@link REPEATED_FAILURE_HINTS}; returns `undefined` when the
 * error is unrecognized so the caller keeps the generic directive unchanged.
 */
export const repeatedFailureHint = (error: string): string | undefined =>
  REPEATED_FAILURE_HINTS.find(({ match }) => error.includes(match))?.hint;

export const flagRepeatedFailure = (envelope: ToolOutputError): ToolOutputError => {
  const hint = repeatedFailureHint(envelope.error);
  const directive = hint === undefined ? REPEATED_FAILURE_DIRECTIVE : `${REPEATED_FAILURE_DIRECTIVE} ${hint}`;
  return {
    ...envelope,
    error: `${directive}${ORIGINAL_ERROR_MARKER}${envelope.error}`,
    recovery: 'retry-with-different-input',
  };
};
